import React, { useEffect, useRef, useState } from 'react';

export const CadLogo = ({ className = '', size = 36 }) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 64 64"
    fill="none"
    aria-hidden="true"
  >
    <circle cx="32" cy="32" r="30" stroke="currentColor" strokeWidth="1.4" />
    <path
      d="M27.5 12.8c-9.6 3.2-16.2 12.4-16.2 23 0 13.5 10.9 24.4 24.4 24.4 4.6 0 8.9-1.3 12.6-3.5C37.4 53.2 26 43.2 26 30.2c0-6.4 2.4-12.2 6.3-16.6-.3-.3-.5-.5-.8-.8z"
      fill="currentColor"
    />
    <circle cx="40" cy="30" r="6.2" stroke="currentColor" strokeWidth="1.6" />
    <circle cx="40" cy="30" r="2.1" fill="currentColor" />
  </svg>
);

function CadCanvas({ children, viewBox = '0 0 480 420', className = '', label }) {
  return (
    <div className="cad-canvas">
      <svg
        className={`cad-svg ${className}`}
        viewBox={viewBox}
        fill="none"
        role="img"
        aria-label={label}
      >
        {children}
      </svg>
    </div>
  );
}

function Corner({ x, y, dx, dy }) {
  return (
    <path
      className="cad-ghost"
      d={`M${x} ${y + dy * 16} V${y} H${x + dx * 16}`}
    />
  );
}

function HatchDef({ id = 'cadHatch' }) {
  return (
    <defs>
      <pattern id={id} width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
        <line className="cad-hatch-line" x1="0" y1="0" x2="0" y2="6" />
      </pattern>
    </defs>
  );
}

function Crop() {
  return (
    <>
      <Corner x={22} y={18} dx={1} dy={1} />
      <Corner x={498} y={18} dx={-1} dy={1} />
      <Corner x={22} y={462} dx={1} dy={-1} />
      <Corner x={498} y={462} dx={-1} dy={-1} />
    </>
  );
}

const AREA_PLATES = {
  digital: (
    <>
      <path d="M18 22 H222 V78 H18 Z" />
      <path d="M18 36 H222" />
      <circle cx="30" cy="29" r="3" className="cad-open" />
      <path className="cad-open" d="M42 29 H88" />
      <path className="cad-ghost" d="M30 46 H118 V70 H30 Z" />
      <path className="cad-ghost" d="M128 46 H210 V56 H128 Z M128 60 H186 V70 H128 Z" />
    </>
  ),
  marketing: (
    <>
      <path d="M28 74 V50 H52 V74 Z M60 74 V36 H84 V74 Z M92 74 V22 H116 V74 Z" />
      <path className="cad-flow" d="M128 66 H208" />
      <path d="M168 28 L208 66 H128 Z" className="cad-ghost" />
      <circle cx="168" cy="28" r="7" className="cad-open" />
    </>
  ),
  ai: (
    <>
      <path d="M24 24 H72 V44 H24 Z M96 18 H144 V40 H96 Z M168 24 H216 V44 H168 Z" />
      <path d="M96 52 H144 V74 H96 Z" className="cad-open" />
      <path className="cad-flow" d="M72 34 H96 M144 29 H168 M120 40 V52" />
    </>
  ),
  business: (
    <>
      <path d="M40 18 H118 V78 H40 Z" />
      <path d="M78 30 H196 V86 H78 Z" className="cad-open" />
      <path className="cad-ghost" d="M52 32 H104 M52 44 H96 M52 56 H88" />
      <path className="cad-ghost" d="M92 44 H180 M92 56 H164 M92 68 H148" />
    </>
  ),
  tenders: (
    <>
      <path d="M28 16 H148 V82 H28 Z" />
      <path className="cad-ghost" d="M40 30 H132 M40 42 H120 M40 54 H128 M40 66 H108" />
      <circle cx="186" cy="48" r="22" className="cad-open" />
      <path d="M186 36 V60 M174 48 H198" className="cad-ghost" />
    </>
  ),
  hospitality: (
    <>
      <path d="M16 20 H224 V78 H16 Z" />
      <path className="cad-ghost" d="M85 20 V78 M155 20 V78" />
      <path d="M16 78 H40" className="cad-open" />
      <path className="cad-ghost" d="M28 32 H72 V52 H28 Z M98 32 H142 V52 H98 Z M168 32 H212 V52 H168 Z" />
      <path className="cad-ghost" d="M32 60 H56 M102 60 H126 M172 60 H196" />
    </>
  ),
};

export const AreaPlate = ({ kind }) => (
  <svg className="cad-svg cad-area-svg" viewBox="0 0 240 96" fill="none" aria-hidden="true">
    {AREA_PLATES[kind] || AREA_PLATES.digital}
  </svg>
);

function TitleBlock({ drawing, sheet, scale }) {
  return (
    <g className="cad-titleblock">
      <rect x="332" y="394" width="154" height="54" />
      <path d="M332 412 H486 M332 430 H486" />
      <text className="cad-anno cad-stamp" x="340" y="408">TRE LUNE</text>
      <text className="cad-anno cad-stamp" x="340" y="426">{drawing}</text>
      <text className="cad-anno cad-stamp" x="340" y="444">
        FOGLIO {sheet} · {scale}
      </text>
    </g>
  );
}

const SheetSectionA = () => (
  <CadCanvas className="cad-hero-svg" viewBox="0 0 520 480" label="Sezione: vetrina a doppia altezza e gestionale interrato">
    <HatchDef />
    <Crop />

    <path className="cad-ghost" d="M80 52 V360" />
    <path className="cad-ghost" d="M74 52 H86 M74 228 H86 M74 248 H86 M74 360 H86" />
    <text className="cad-anno" x="70" y="56" textAnchor="end">+7.20</text>
    <text className="cad-anno" x="70" y="232" textAnchor="end">+0.40</text>
    <text className="cad-anno" x="70" y="252" textAnchor="end">±0.00</text>
    <text className="cad-anno" x="70" y="364" textAnchor="end">−3.60</text>
    <text className="cad-anno" x="80" y="44">SEZ. A–A</text>

    <path className="cad-ghost" d="M40 248 H484" />

    <path
      className="cad-hatch"
      fillRule="evenodd"
      d="M40 248 H136 V360 H40 Z M396 248 H480 V360 H396 Z"
    />

    <path
      className="cad-hatch"
      fillRule="evenodd"
      d="M136 52 H396 V360 H136 Z M148 64 H252 V228 H148 Z M264 64 H384 V144 H264 Z M264 156 H384 V228 H264 Z M148 248 H264 V348 H148 Z M276 248 H384 V348 H276 Z"
    />
    <path d="M136 52 H396 V360 H136 Z" />
    <path d="M108 52 H136 V78 H108 Z" />

    <path className="cad-ghost" d="M164 108 H236 V200 H164 Z" />
    <path className="cad-ghost" d="M176 128 H220 M176 148 H204" />
    <text className="cad-anno" x="164" y="92">VETRINA</text>

    <path className="cad-ghost" d="M276 108 H372 V136 H276 Z" />
    <text className="cad-anno" x="276" y="92">SITO</text>
    <path className="cad-ghost" d="M276 168 H360 V216 H276 Z" />

    <path className="cad-ghost" d="M164 268 H248 V328 H164 Z M292 268 H372 V328 H292 Z" />
    <text className="cad-anno" x="164" y="262">GESTIONALE</text>
    <text className="cad-anno" x="292" y="262">DATI</text>

    <path className="cad-flow" d="M196 200 V268" />

    <path className="cad-ghost" d="M136 372 H396" />
    <path className="cad-ghost" d="M136 368 V376 M396 368 V376" />
    <text className="cad-anno" x="248" y="384">9.80</text>

    <TitleBlock drawing="SEZ. A–A" sheet="04" scale="1 : 50" />
  </CadCanvas>
);

const SheetPlan = () => (
  <CadCanvas className="cad-hero-svg" viewBox="0 0 520 480" label="Pianta: vetrina del sito e locali del gestionale">
    <HatchDef />
    <Crop />
    <path className="cad-ghost" d="M430 40 V62" />
    <path d="M430 28 L424 46 L430 40 L436 46 Z" className="cad-fill" />
    <text className="cad-anno" x="438" y="38">N</text>

    <path
      className="cad-hatch"
      fillRule="evenodd"
      d="M118 70 H428 V348 H118 Z M132 84 H414 V334 H132 Z"
    />
    <path d="M118 70 H428 V348 H118 Z" />
    <path className="cad-ghost" d="M132 84 H414 V334 H132 Z" />
    <path d="M248 348 H298" className="cad-open" />
    <path className="cad-ghost" d="M298 348 A50 50 0 0 0 248 348" />

    <path className="cad-ghost" d="M132 188 H414" />
    <text className="cad-anno" x="144" y="104">SITO — VETRINA</text>
    <path className="cad-ghost" d="M160 118 H386 V170 H160 Z" />
    <path className="cad-ghost" d="M176 132 H250 M176 148 H310" />

    <path className="cad-ghost" d="M273 188 V334 M132 261 H414" />
    <text className="cad-anno" x="144" y="210">GESTIONALE</text>
    <text className="cad-anno" x="144" y="248">CLIENTI</text>
    <text className="cad-anno" x="286" y="248">PREVENTIVI</text>
    <text className="cad-anno" x="144" y="322">AGENDA</text>
    <text className="cad-anno" x="286" y="322">DATI</text>

    <path className="cad-ghost" d="M118 362 H428" />
    <path className="cad-ghost" d="M118 356 V368 M428 356 V368" />
    <text className="cad-anno" x="248" y="380">12.40</text>
    <TitleBlock drawing="PIANTA" sheet="02" scale="1 : 100" />
  </CadCanvas>
);

const SheetElevation = () => (
  <CadCanvas className="cad-hero-svg" viewBox="0 0 520 480" label="Prospetto: facciata del sito e fondazione del gestionale">
    <HatchDef />
    <Crop />
    <path className="cad-ghost" d="M94 88 V300" />
    <path className="cad-ghost" d="M88 88 H100 M88 300 H100 M88 368 H100" />
    <text className="cad-anno" x="84" y="92" textAnchor="end">+6.40</text>
    <text className="cad-anno" x="84" y="304" textAnchor="end">±0.00</text>
    <text className="cad-anno" x="84" y="372" textAnchor="end">−2.20</text>

    <path className="cad-ghost" d="M40 300 H480" />
    <path d="M154 88 H400 V300 H154 Z" className="cad-wash" />
    <path d="M154 88 H400 V300 H154 Z" />
    <path d="M154 88 H400 V104 H154 Z" className="cad-fill" />
    <path className="cad-ghost" d="M215 88 V300 M277 88 V300 M339 88 V300" />
    <path className="cad-ghost" d="M168 120 H200 V250 H168 Z M230 120 H262 V250 H230 Z M292 120 H324 V250 H292 Z M354 120 H386 V250 H354 Z" />
    <path d="M256 252 H298 V300 H256 Z" />
    <text className="cad-anno" x="154" y="78">SITO — FACCIATA</text>

    <path d="M154 300 H400 V368 H154 Z" className="cad-hatch" />
    <path className="cad-cut" d="M154 300 H400 V368 H154 Z" />
    <text className="cad-anno" x="154" y="384">GESTIONALE</text>
    <TitleBlock drawing="PROSPETTO" sheet="03" scale="1 : 50" />
  </CadCanvas>
);

const SheetDetail = () => (
  <CadCanvas className="cad-hero-svg" viewBox="0 0 520 480" label="Dettaglio del nodo: solaio tra sito e gestionale">
    <HatchDef />
    <Crop />

    <circle cx="92" cy="92" r="30" className="cad-ghost" />
    <path className="cad-ghost" d="M78 74 H106 V86 H78 Z M78 92 H106 V110 H78 Z" />
    <path d="M72 86 H112 V94 H72 Z" className="cad-fill" />
    <circle cx="100" cy="90" r="8" />
    <text className="cad-anno" x="80" y="134">N1</text>

    <text className="cad-anno" x="176" y="44">NODO 1</text>

    <path className="cad-ghost" d="M164 56 V352" />
    <path className="cad-ghost" d="M158 56 H170 M158 168 H170 M158 192 H170 M158 352 H170" />
    <text className="cad-anno" x="152" y="60" textAnchor="end">SITO</text>
    <text className="cad-anno" x="152" y="176" textAnchor="end">NODO</text>
    <text className="cad-anno" x="152" y="356" textAnchor="end">±0</text>

    <path d="M184 56 H220 V352 H184 Z" />
    <path d="M188 60 H216 V348 H188 Z" className="cad-hatch" />
    <path className="cad-ghost" d="M196 72 H208 V136 H196 Z" />

    <path d="M220 56 H400 V168 H220 Z" className="cad-wash" />
    <path d="M220 56 H400 V168 H220 Z" />
    <path className="cad-ghost" d="M236 76 H384 V148 H236 Z" />
    <path className="cad-ghost" d="M248 92 H332 M248 112 H360 M248 132 H300" />
    <text className="cad-anno" x="236" y="70">SITO</text>

    <path d="M176 168 H408 V192 H176 Z" className="cad-fill" />
    <circle cx="220" cy="180" r="18" className="cad-open" />
    <text className="cad-anno" x="248" y="162">SOLAIO</text>

    <path d="M220 192 H400 V352 H220 Z" className="cad-hatch" />
    <path d="M220 192 H400 V352 H220 Z" />
    <path className="cad-ghost" d="M236 212 H316 V256 H236 Z M332 212 H384 V256 H332 Z M236 272 H384 V332 H236 Z" />
    <text className="cad-anno" x="236" y="346">GESTIONALE</text>

    <path className="cad-flow" d="M252 148 V220" />
    <TitleBlock drawing="DETTAGLIO" sheet="01" scale="1 : 10" />
  </CadCanvas>
);

export const CadEcosystem = () => (
  <CadCanvas className="cad-hero-svg" viewBox="0 0 520 480" label="Pianta dell’ecosistema Tre Lune: sei vani, un solo nucleo">
    <HatchDef />
    <Crop />
    <path className="cad-ghost" d="M430 40 V62" />
    <path d="M430 28 L424 46 L430 40 L436 46 Z" className="cad-fill" />
    <text className="cad-anno" x="438" y="38">N</text>

    <path
      className="cad-hatch"
      fillRule="evenodd"
      d="M100 64 H428 V348 H100 Z M118 82 H410 V330 H118 Z"
    />
    <path d="M100 64 H428 V348 H100 Z" />
    <path className="cad-ghost" d="M118 82 H410 V330 H118 Z" />
    <path className="cad-ghost" d="M215 82 V186 M312 82 V186 M215 228 V330 M312 228 V330 M118 186 H410 M118 228 H410" />

    <text className="cad-anno" x="130" y="104">DIGITALE</text>
    <path className="cad-ghost" d="M130 118 H200 V168 H130 Z" />
    <path className="cad-ghost" d="M140 132 H188 M140 148 H176" />

    <text className="cad-anno" x="227" y="104">AI</text>
    <path className="cad-ghost" d="M227 118 H296 V168 H227 Z" />

    <text className="cad-anno" x="324" y="104">CRESCITA</text>
    <path className="cad-ghost" d="M324 118 H394 V168 H324 Z" />

    <text className="cad-anno" x="130" y="214">CORE — TRE LUNE</text>

    <text className="cad-anno" x="130" y="252">LEGALE</text>
    <path className="cad-ghost" d="M130 266 H200 V314 H130 Z" />

    <text className="cad-anno" x="227" y="252">VACANZE</text>
    <path className="cad-ghost" d="M227 266 H296 V314 H227 Z" />

    <text className="cad-anno" x="324" y="252">GARE</text>
    <path className="cad-ghost" d="M324 266 H394 V314 H324 Z" />

    <path className="cad-ghost" d="M100 362 H428" />
    <path className="cad-ghost" d="M100 356 V368 M428 356 V368" />
    <text className="cad-anno" x="236" y="380">12.40</text>
    <TitleBlock drawing="PIANTA" sheet="01" scale="1 : 100" />
  </CadCanvas>
);

function CenterMark({ x, y }) {
  return <path className="cad-ghost" d={`M${x - 7} ${y} H${x + 7} M${x} ${y - 7} V${y + 7}`} />;
}

function Balloon({ x, y, n }) {
  return (
    <g>
      <circle cx={x} cy={y} r="9" className="cad-open" />
      <text className="cad-anno" x={x} y={y + 4} textAnchor="middle">{n}</text>
    </g>
  );
}

export const CadTeam = () => {
  // Proportions from public/media/3LT_Logo.png: white disc NE of the outer,
  // nucleus coaxial with the outer and internally tangent to the white disc.
  const Ra = 58;
  const Rb = Ra * 0.61753;
  const Rc = Ra * 0.23531;
  const A = { x: 118, y: 202 };
  const B = { x: A.x + Ra * 0.26245, y: A.y - Ra * 0.27839 };
  const C = A;
  const Ae = { x: 286, y: 292 };
  const Be = { x: 372, y: 186 };
  const Ce = { x: 438, y: 86 };
  const rim = (from, to, r) => {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const len = Math.hypot(dx, dy) || 1;
    return { x: from.x + (dx / len) * r, y: from.y + (dy / len) * r };
  };
  const aRim = rim(A, Ae, Ra + 6);
  const bRim = rim(A, Be, Ra + 6);
  const cRim = rim(A, Ce, Ra + 6);

  return (
    <CadCanvas className="cad-hero-svg" viewBox="0 0 520 480" label="Esploso del marchio Tre Lune: tre dischi">
      <HatchDef id="cadHatchTeam" />
      <Crop />
      <text className="cad-anno" x="88" y="48">ESPLOSO MARCHIO</text>

      <path className="cad-cut" d={`M${aRim.x} ${aRim.y} L${Ae.x} ${Ae.y} M${bRim.x} ${bRim.y} L${Be.x} ${Be.y} M${cRim.x} ${cRim.y} L${Ce.x} ${Ce.y}`} />

      <circle cx={A.x} cy={A.y} r={Ra} className="cad-fill" />
      <circle cx={B.x} cy={B.y} r={Rb} className="cad-paper-fill" />
      <circle cx={C.x} cy={C.y} r={Rc} className="cad-fill" />
      <text className="cad-anno" x={A.x} y={A.y + Ra + 22} textAnchor="middle">ASSEMBLATO</text>

      <circle cx={Ae.x} cy={Ae.y} r={Ra} className="cad-hatch-team" />
      <circle cx={Ae.x} cy={Ae.y} r={Ra} />
      <CenterMark x={Ae.x} y={Ae.y} />
      <Balloon x={Ae.x - 70} y={Ae.y} n="1" />
      <text className="cad-anno" x={Ae.x} y={Ae.y + Ra + 20} textAnchor="middle">Ø {Math.round(Ra * 2)} · NERO</text>

      <circle cx={Be.x} cy={Be.y} r={Rb} className="cad-paper" />
      <CenterMark x={Be.x} y={Be.y} />
      <Balloon x={Be.x - 54} y={Be.y} n="2" />
      <text className="cad-anno" x={Be.x} y={Be.y + Rb + 20} textAnchor="middle">Ø {Math.round(Rb * 2)} · CARTA</text>

      <circle cx={Ce.x} cy={Ce.y} r={Rc} className="cad-fill" />
      <circle cx={Ce.x} cy={Ce.y} r={Rc} />
      <CenterMark x={Ce.x} y={Ce.y} />
      <Balloon x={Ce.x - 34} y={Ce.y} n="3" />
      <text className="cad-anno" x={Ce.x + 22} y={Ce.y + 4}>Ø {Math.round(Rc * 2)} · COAX.</text>

      <path className="cad-ghost" d="M40 348 H210 V418 H40 Z" />
      <path className="cad-ghost" d="M40 366 H210 M40 384 H210 M40 402 H210" />
      <text className="cad-stamp cad-anno" x="48" y="362">PZ</text>
      <text className="cad-stamp cad-anno" x="78" y="362">PEZZO</text>
      <text className="cad-stamp cad-anno" x="48" y="380">1</text>
      <text className="cad-stamp cad-anno" x="78" y="380">DISCO EST.</text>
      <text className="cad-stamp cad-anno" x="48" y="398">2</text>
      <text className="cad-stamp cad-anno" x="78" y="398">DISCO INT.</text>
      <text className="cad-stamp cad-anno" x="48" y="416">3</text>
      <text className="cad-stamp cad-anno" x="78" y="416">NUCLEO · C/1</text>

      <TitleBlock drawing="ESPLOSO" sheet="03" scale="1 : 1" />
    </CadCanvas>
  );
};

const HERO_SHEETS = [SheetDetail, SheetPlan, SheetElevation, SheetSectionA];

export function CadHeroScene() {
  const [index, setIndex] = useState(0);
  const paused = useRef(false);
  const timer = useRef(null);

  useEffect(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (reduce.matches) return undefined;

    const play = () => {
      window.clearInterval(timer.current);
      timer.current = window.setInterval(() => {
        if (!paused.current) setIndex((n) => (n + 1) % HERO_SHEETS.length);
      }, 5200);
    };

    play();
    return () => window.clearInterval(timer.current);
  }, []);

  const goTo = (i) => {
    setIndex(i);
    window.clearInterval(timer.current);
    timer.current = window.setInterval(() => {
      if (!paused.current) setIndex((n) => (n + 1) % HERO_SHEETS.length);
    }, 5200);
  };

  const Sheet = HERO_SHEETS[index];

  return (
    <div
      className="cad-hero-sheets"
      onMouseEnter={() => { paused.current = true; }}
      onMouseLeave={() => { paused.current = false; }}
    >
      <Sheet key={index} />
      <div className="cad-sheet-pager" role="tablist" aria-label="Fogli di progetto">
        {HERO_SHEETS.map((_, i) => (
          <button
            key={i}
            type="button"
            role="tab"
            aria-label={`Foglio ${String(i + 1).padStart(2, '0')}`}
            aria-selected={i === index}
            className={i === index ? 'is-on' : undefined}
            onClick={() => goTo(i)}
          >
            {String(i + 1).padStart(2, '0')}
          </button>
        ))}
      </div>
    </div>
  );
}

export const CadSite = () => (
  <CadCanvas viewBox="0 0 480 360" label="Sito custom contro template WordPress">
    <text x="28" y="32" className="cad-anno">A — TEMPLATE WP</text>
    <text x="268" y="32" className="cad-anno">B — CUSTOM BUILD</text>

    {[0, 1, 2].map((i) => (
      <g key={i} opacity={1 - i * 0.18} transform={`translate(${24 + i * 10} ${48 + i * 14})`}>
        <rect x="0" y="0" width="176" height="88" />
        <rect x="10" y="10" width="48" height="68" />
        <path d="M70 18 h90 M70 34 h70 M70 50 h80 M70 66 h54" />
      </g>
    ))}
    <text x="28" y="330" className="cad-anno cad-mute">STESSO TEMA × N</text>

    <path className="cad-flow" d="M214 180 H252" />

    <g transform="translate(268 48)">
      <rect x="0" y="0" width="188" height="248" className="cad-open" />
      <path d="M0 36 H188" />
      <circle cx="18" cy="18" r="6" className="cad-open" />
      <path className="cad-open" d="M36 18 H92" />
      <rect x="12" y="52" width="164" height="72" />
      <path d="M24 68 h80 M24 84 h112 M24 100 h64" className="cad-ghost" />
      <rect x="12" y="136" width="76" height="92" className="cad-open" />
      <rect x="100" y="136" width="76" height="42" />
      <rect x="100" y="186" width="76" height="42" />
      <path d="M24 220 h40" className="cad-open" />
    </g>
  </CadCanvas>
);

export const CadGestionale = () => (
  <CadCanvas viewBox="0 0 480 360" label="Gestionale proprietario senza costo a slot">
    <rect x="40" y="40" width="400" height="280" />
    <path className="cad-ghost" d="M40 88 H440" />
    <text x="52" y="70" className="cad-anno">CORE — PROPRIETARIO</text>
    <text x="292" y="70" className="cad-anno cad-open-fill">NO € / SLOT</text>

    {[
      { x: 60, y: 112, t: 'CLIENTI' },
      { x: 186, y: 112, t: 'PREVENTIVI' },
      { x: 312, y: 112, t: 'ORDINI' },
      { x: 60, y: 214, t: 'MAGAZZINO' },
      { x: 186, y: 214, t: 'AGENDA' },
      { x: 312, y: 214, t: 'SU MISURA' },
    ].map((mod, i) => (
      <g key={mod.t}>
        <rect
          x={mod.x}
          y={mod.y}
          width="108"
          height="78"
          className={i === 5 ? 'cad-open' : ''}
        />
        <path
          className="cad-ghost"
          d={`M${mod.x + 12} ${mod.y + 28} h84 M${mod.x + 12} ${mod.y + 44} h64`}
        />
        <text x={mod.x + 12} y={mod.y + 66} className="cad-anno">
          {mod.t}
        </text>
      </g>
    ))}

    <path
      className="cad-flow"
      d="M114 190 V214 M240 190 V214 M366 190 V214 M168 151 H186 M294 151 H312"
    />
  </CadCanvas>
);
